import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import * as session from './session';
import type { PersonId, Role } from './types';

type GameContextValue = {
  state: session.GameState;
  start: (seed?: string) => void;
  draft: (personId: PersonId, role: Role) => void;
  reroll: () => void;
};

const GameContext = createContext<GameContextValue | null>(null);

export function GameProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<session.GameState>(() => session.newGame());

  const start = useCallback((seed?: string) => setState(session.newGame(seed)), []);
  const draft = useCallback(
    (personId: PersonId, role: Role) => setState((prev) => session.draft(prev, personId, role)),
    [],
  );
  const reroll = useCallback(() => setState((prev) => session.reroll(prev)), []);

  const value = useMemo(() => ({ state, start, draft, reroll }), [state, start, draft, reroll]);

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

export function useGame() {
  const value = useContext(GameContext);
  if (!value) throw new Error('useGame must be used inside <GameProvider>');
  return value;
}
